import * as React from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Armchair, Ban, CheckCircle2, Loader2, Radio, UserPlus } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import type { ApiError } from "@eduviaos/shared";
import { FloorBackdrop, OccupancyLegend } from "@/components/seating/FloorCanvas";
import { SEAT_STATUS_CLASSES, SEAT_STATUS_SWATCH, seatTransform } from "@/lib/seating/floorStyles";
import { useCanManageBookings, useCanEditSeating } from "@/hooks/useSeatingAuthoring";
import { useCancelBooking, useCheckInBooking } from "@/lib/seating/bookings";
import {
  OCCUPANCY_STATUS_LABELS,
  occupancyKeys,
  todayKey,
  useFloorPlan,
  useOccupancy,
  useSeatingRealtime,
  type FloorPlanBody,
  type OccupancyResponse,
  type SeatOccupancyItem,
  type SeatOccupancyStatus,
} from "@/lib/seating/floorPlan";
import { useLearnerSeatPlans, usePutLearnerSeatPlan } from "@/lib/seating/plans";
import { useSeatConfig } from "@/lib/seatAssignmentConfig/api";
import { useCreateSeatAssignment } from "@/lib/seating/allocations";
import { formatClock, sortSlots, useSeatSlots } from "@/lib/seatSlots/api";

/**
 * Phase SC-22c — live floor (feature 4.70).
 *
 * The saved floor plan coloured by today's (or a chosen date's) seat
 * status, for one shift or the whole day. Seat events arrive over the
 * seating websocket and patch colours straight away; while the socket is
 * down the occupancy query polls every 15 seconds. Clicking a seat opens
 * a drawer to check the learner in, cancel the booking, or give the seat
 * to a learner.
 */

const WHOLE_DAY = "__day__";
const POLL_MS = 15_000;

export function LiveFloorPage() {
  const { canManageBookings } = useCanManageBookings();
  const { canEditSeating } = useCanEditSeating();
  const [dateKey, setDateKey] = React.useState(todayKey);
  const [slotKey, setSlotKey] = React.useState<string>(WHOLE_DAY);
  const [openSeatId, setOpenSeatId] = React.useState<string | null>(null);

  const realtime = useSeatingRealtime(null);
  const occupancy = useOccupancy(dateKey, slotKey === WHOLE_DAY ? null : slotKey, null, {
    pollMs: realtime === "live" ? false : POLL_MS,
  });
  const planQuery = useFloorPlan(null);
  const slotsQuery = useSeatSlots();
  const slots = sortSlots(slotsQuery.data?.items ?? []).filter((s) => s.is_active);

  const data = occupancy.data;
  const counts = React.useMemo(() => countByStatus(data), [data]);
  const openSeat = data?.seats.find((s) => s.seat_id === openSeatId) ?? null;
  const zoneName = React.useMemo(
    () => new Map((data?.zones ?? []).map((z) => [z.zone_id, z.zone_name])),
    [data],
  );

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Live floor</PageHeaderHeading>
            <PageHeaderDescription>
              Who is booked, who has arrived, and which seats are free right now.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      <div className="flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="live-date">Date</Label>
          <Input
            id="live-date"
            type="date"
            value={dateKey}
            onChange={(e) => e.target.value && setDateKey(e.target.value)}
            className="w-44"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="live-shift">Shift</Label>
          <Select value={slotKey} onValueChange={setSlotKey}>
            <SelectTrigger id="live-shift" className="w-52" aria-label="Shift">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={WHOLE_DAY}>Whole day</SelectItem>
              {slots.map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.label} · {formatClock(s.start_time)}–{formatClock(s.end_time)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span
          className={cn(
            "ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs",
            realtime === "live"
              ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-300"
              : "border-border text-muted-foreground",
          )}
          data-testid="live-floor-transport"
        >
          <Radio aria-hidden="true" className="size-3.5" />
          {realtime === "live" ? "Live" : "Updating every 15 seconds"}
        </span>
      </div>

      <Card>
        <CardHeader className="gap-3 pb-3 sm:flex-row sm:items-center sm:justify-between">
          <OccupancyLegend counts={counts} />
          {data && data.walk_ins > 0 ? (
            <span className="text-muted-foreground text-xs tabular-nums">
              {data.walk_ins} walk-in{data.walk_ins === 1 ? "" : "s"} checked in without a seat
            </span>
          ) : null}
        </CardHeader>
        <CardContent className="pt-0">
          {occupancy.isError && !data ? (
            <div
              role="alert"
              className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
              <span className="flex-1">Couldn&apos;t load seat status.</span>
              <Button variant="ghost" size="sm" onClick={() => void occupancy.refetch()}>
                Retry
              </Button>
            </div>
          ) : !data || planQuery.isLoading ? (
            <div
              aria-busy="true"
              aria-label="Loading seats"
              className="bg-muted/60 aspect-[3/2] w-full animate-pulse rounded-lg motion-reduce:animate-none"
            />
          ) : planQuery.data ? (
            <LiveFloorMap plan={planQuery.data} occupancy={data} onOpen={setOpenSeatId} />
          ) : (
            <>
              <p className="text-muted-foreground mb-3 text-xs">
                No floor plan yet.{" "}
                {canEditSeating ? (
                  <Link to="/seating/floor-plan" className="text-primary font-medium underline">
                    Draw one
                  </Link>
                ) : null}{" "}
                Seats are shown by room until then.
              </p>
              <SeatTileGrid occupancy={data} zoneName={zoneName} onOpen={setOpenSeatId} />
            </>
          )}
        </CardContent>
      </Card>

      <SeatDrawer
        seat={openSeat}
        zoneLabel={openSeat ? (zoneName.get(openSeat.zone_id) ?? null) : null}
        canManageBookings={canManageBookings}
        canAssign={canEditSeating}
        onClose={() => setOpenSeatId(null)}
      />
    </div>
  );
}

function useRefreshOccupancy() {
  const qc = useQueryClient();
  return React.useCallback(() => {
    void qc.invalidateQueries({ queryKey: occupancyKeys.all });
  }, [qc]);
}

function countByStatus(data: OccupancyResponse | undefined) {
  const counts: Partial<Record<SeatOccupancyStatus, number>> = {};
  for (const s of data?.seats ?? []) counts[s.status] = (counts[s.status] ?? 0) + 1;
  return counts;
}

function seatAriaLabel(seat: SeatOccupancyItem): string {
  const who = seat.learner_name ? `, ${seat.learner_name}` : "";
  return `Seat ${seat.seat_number}, ${OCCUPANCY_STATUS_LABELS[seat.status].toLowerCase()}${who}`;
}

function LiveFloorMap({
  plan,
  occupancy,
  onOpen,
}: {
  plan: FloorPlanBody;
  occupancy: OccupancyResponse;
  onOpen: (seatId: string) => void;
}) {
  const byId = React.useMemo(
    () => new Map(occupancy.seats.map((s) => [s.seat_id, s])),
    [occupancy.seats],
  );
  const placed = plan.seats.filter((s) => s.seat_id && byId.has(s.seat_id));
  const unplaced = occupancy.seats.filter((s) => !plan.seats.some((p) => p.seat_id === s.seat_id));
  const fontSize = 3.2;

  return (
    <div className="space-y-4">
      <div className="bg-muted/30 rounded-lg p-2 sm:p-4">
        <svg
          viewBox={`0 0 ${plan.width} ${plan.height}`}
          className="h-auto max-h-[70vh] w-full select-none"
          role="group"
          aria-label={`${plan.name}, ${occupancy.seats.length} seats`}
          data-testid="live-floor-map"
        >
          <FloorBackdrop plan={plan} />
          {placed.map((placement) => {
            const seat = byId.get(placement.seat_id!)!;
            return (
              <g
                key={placement.seat_id}
                transform={seatTransform(placement)}
                role="button"
                tabIndex={0}
                aria-label={seatAriaLabel(seat)}
                data-testid={`live-seat-${seat.seat_number}`}
                data-status={seat.status}
                className="cursor-pointer outline-none [&:focus-visible>rect]:stroke-[1.2]"
                onClick={() => onOpen(seat.seat_id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen(seat.seat_id);
                  }
                }}
              >
                <rect
                  x={placement.x}
                  y={placement.y}
                  width={placement.w}
                  height={placement.h}
                  rx={1.2}
                  strokeWidth={0.5}
                  className={cn(
                    "transition-[fill,stroke] duration-200 ease-out motion-reduce:transition-none",
                    SEAT_STATUS_CLASSES[seat.status],
                  )}
                />
                <text
                  x={placement.x + placement.w / 2}
                  y={placement.y + placement.h / 2}
                  fontSize={fontSize}
                  textAnchor="middle"
                  dominantBaseline="central"
                  className="fill-foreground pointer-events-none font-medium tabular-nums"
                >
                  {seat.seat_number}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {unplaced.length > 0 ? (
        <div className="space-y-2">
          <p className="text-muted-foreground text-xs">Seats not on the floor plan yet</p>
          <SeatTileGrid
            occupancy={{ ...occupancy, seats: unplaced }}
            zoneName={new Map(occupancy.zones.map((z) => [z.zone_id, z.zone_name]))}
            onOpen={onOpen}
          />
        </div>
      ) : null}
    </div>
  );
}

function SeatTileGrid({
  occupancy,
  zoneName,
  onOpen,
}: {
  occupancy: OccupancyResponse;
  zoneName: Map<string, string>;
  onOpen: (seatId: string) => void;
}) {
  const groups = React.useMemo(() => {
    const byZone = new Map<string, SeatOccupancyItem[]>();
    for (const s of occupancy.seats) {
      const list = byZone.get(s.zone_id) ?? [];
      list.push(s);
      byZone.set(s.zone_id, list);
    }
    return [...byZone.entries()].map(([zoneId, seats]) => ({
      zoneId,
      seats: [...seats].sort((a, b) =>
        a.seat_number.localeCompare(b.seat_number, undefined, { numeric: true }),
      ),
    }));
  }, [occupancy.seats]);

  if (occupancy.seats.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center">
        <Armchair aria-hidden="true" className="text-muted-foreground size-6" />
        <p className="text-sm font-medium">No seats yet</p>
        <p className="text-muted-foreground text-xs">Add seats or draw a floor plan first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map(({ zoneId, seats }) => (
        <div key={zoneId} className="space-y-2">
          <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
            {zoneName.get(zoneId) ?? "Room"}
          </p>
          <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-8 lg:grid-cols-12">
            {seats.map((seat) => (
              <button
                key={seat.seat_id}
                type="button"
                aria-label={seatAriaLabel(seat)}
                onClick={() => onOpen(seat.seat_id)}
                className={cn(
                  "flex aspect-square items-center justify-center rounded-md border text-xs font-semibold tabular-nums transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] motion-reduce:transition-none",
                  SEAT_STATUS_SWATCH[seat.status],
                )}
              >
                {seat.seat_number}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SeatDrawer({
  seat,
  zoneLabel,
  canManageBookings,
  canAssign,
  onClose,
}: {
  seat: SeatOccupancyItem | null;
  zoneLabel: string | null;
  canManageBookings: boolean;
  canAssign: boolean;
  onClose: () => void;
}) {
  const checkIn = useCheckInBooking();
  const cancel = useCancelBooking();
  const refreshOccupancy = useRefreshOccupancy();
  const [cancelReason, setCancelReason] = React.useState("");
  const [cancelling, setCancelling] = React.useState(false);

  React.useEffect(() => {
    setCancelReason("");
    setCancelling(false);
  }, [seat?.seat_id]);

  const bookingId = seat?.booking_id ?? null;
  const canCheckIn = canManageBookings && bookingId && seat?.booking_status === "held";
  const canCancel =
    canManageBookings &&
    bookingId &&
    (seat?.booking_status === "held" || seat?.booking_status === "checked_in");

  return (
    <Sheet open={seat !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-5 overflow-y-auto sm:max-w-sm">
        {seat ? (
          <>
            <SheetHeader>
              <SheetTitle>Seat {seat.seat_number}</SheetTitle>
              <SheetDescription>
                {zoneLabel ? `${zoneLabel} · ` : ""}
                {OCCUPANCY_STATUS_LABELS[seat.status]}
                {seat.learner_name ? ` · ${seat.learner_name}` : ""}
              </SheetDescription>
            </SheetHeader>

            {canCheckIn ? (
              <Button
                onClick={() =>
                  checkIn.mutate(
                    { id: bookingId!, body: {} },
                    {
                      onSuccess: () => {
                        refreshOccupancy();
                        toast.success(`${seat.learner_name ?? "Learner"} checked in`);
                      },
                      onError: (err: ApiError) =>
                        toast.error(err.message || "Couldn't check the learner in"),
                    },
                  )
                }
                disabled={checkIn.isPending}
              >
                {checkIn.isPending ? (
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                ) : (
                  <CheckCircle2 aria-hidden="true" className="size-4" />
                )}
                Check in
              </Button>
            ) : null}

            {canCancel ? (
              cancelling ? (
                <form
                  className="grid gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    cancel.mutate(
                      { id: bookingId!, body: { reason: cancelReason.trim() } },
                      {
                        onSuccess: () => {
                          refreshOccupancy();
                          toast.success("Booking cancelled");
                          setCancelling(false);
                        },
                        onError: (err: ApiError) =>
                          toast.error(err.message || "Couldn't cancel the booking"),
                      },
                    );
                  }}
                >
                  <Label htmlFor="cancel-reason">Why is it being cancelled?</Label>
                  <Textarea
                    id="cancel-reason"
                    rows={2}
                    maxLength={2000}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => setCancelling(false)}>
                      Keep booking
                    </Button>
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={!cancelReason.trim() || cancel.isPending}
                    >
                      Cancel booking
                    </Button>
                  </div>
                </form>
              ) : (
                <Button variant="outline" onClick={() => setCancelling(true)}>
                  <Ban aria-hidden="true" className="size-4" />
                  Cancel booking
                </Button>
              )
            ) : null}

            {canAssign && seat.status === "free" ? (
              <AssignSeatForm seat={seat} onDone={onClose} />
            ) : null}

            {!canCheckIn && !canCancel && !(canAssign && seat.status === "free") ? (
              <p className="text-muted-foreground text-sm">
                {seat.status === "blocked"
                  ? "This seat is switched off. Turn it back on from Seats."
                  : seat.status === "fixed"
                    ? "This seat is held as someone's permanent seat or fixed hours."
                    : "Nothing to do for this seat right now."}
              </p>
            ) : null}
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Give a free seat to a learner. In a mixed centre this saves a permanent-
 * seat plan (which also creates the assignment); in fixed mode it creates
 * the permanent assignment directly.
 */
function AssignSeatForm({ seat, onDone }: { seat: SeatOccupancyItem; onDone: () => void }) {
  const config = useSeatConfig();
  const refreshOccupancy = useRefreshOccupancy();
  const mode = config.data?.mode;
  const [q, setQ] = React.useState("");
  const [studentId, setStudentId] = React.useState<string>("");
  const deferredQ = React.useDeferredValue(q);
  const learners = useLearnerSeatPlans({ q: deferredQ, limit: 20, offset: 0 });
  const putPlan = usePutLearnerSeatPlan();
  const assign = useCreateSeatAssignment();
  const pending = putPlan.isPending || assign.isPending;

  if (mode !== "mixed" && mode !== "fixed") {
    return (
      <p className="text-muted-foreground text-sm">
        Learners book this seat themselves. Permanent seats need fixed or mixed seating.
      </p>
    );
  }

  function submit() {
    if (!studentId) return;
    const name = learners.data?.items.find((l) => l.student_id === studentId)?.learner_name;
    const done = {
      onSuccess: () => {
        refreshOccupancy();
        toast.success(`Seat ${seat.seat_number} given to ${name ?? "the learner"}`);
        onDone();
      },
      onError: (err: ApiError) => toast.error(err.message || "Couldn't assign the seat"),
    };
    if (mode === "mixed") {
      putPlan.mutate(
        { studentId, body: { kind: "fixed_seat", seat_id: seat.seat_id, zone_id: seat.zone_id } },
        done,
      );
    } else {
      assign.mutate({ student_id: studentId, seat_id: seat.seat_id }, done);
    }
  }

  return (
    <Card className="shadow-none">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Give this seat to a learner</CardTitle>
        <CardDescription className="text-xs">
          It becomes their permanent seat. Nobody else can book it.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        <Input
          type="search"
          placeholder="Search learners…"
          aria-label="Search learners"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div role="radiogroup" aria-label="Learner" className="grid max-h-56 gap-1 overflow-y-auto">
          {(learners.data?.items ?? []).map((l) => (
            <button
              key={l.student_id}
              type="button"
              role="radio"
              aria-checked={studentId === l.student_id}
              onClick={() => setStudentId(l.student_id)}
              className={cn(
                "flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors duration-150 ease-out motion-reduce:transition-none",
                studentId === l.student_id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/40",
              )}
            >
              <span className="font-medium">{l.learner_name}</span>
              {l.seat_number ? (
                <span className="text-muted-foreground text-xs">Seat {l.seat_number}</span>
              ) : null}
            </button>
          ))}
          {learners.data && learners.data.items.length === 0 ? (
            <p className="text-muted-foreground px-1 py-2 text-xs">No learners match.</p>
          ) : null}
        </div>
        <Button onClick={submit} disabled={!studentId || pending}>
          {pending ? (
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          ) : (
            <UserPlus aria-hidden="true" className="size-4" />
          )}
          Assign seat
        </Button>
      </CardContent>
    </Card>
  );
}
